class CtmSearch extends HTMLElement {
  static decodeBase64(value) {
    if (!value) return '';
    try {
      return atob(value);
    } catch (_err) {
      return '';
    }
  }

  static escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  static debounce(fn, wait = 300) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  }

  static getHeaderElement() {
    return document.querySelector('#ctm-header');
  }

  static setHeaderOverlayState(overlay = 'none') {
    const headerEl = CtmSearch.getHeaderElement();
    if (!headerEl) return;
    headerEl.dataset.overlay = overlay || 'none';
  }

  connectedCallback() {
    this.defaultKeywords = CtmSearch.decodeBase64(this.dataset.defaultKey || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 8);
    this.prevProducts = [];
    this.recentlyHandles = [];
    this.recentlyLists = new Map();
    this.recentlyKeys = [];

    this.icon = this.querySelector('.ctm-search__icon');
    this.searchInputWrap = this.querySelector('.ctm-search__input');
    this.backBtn = this.querySelector('.ctm-search__back');
    this.closeBtn = this.querySelector('.ctm-search__close');
    this.form = this.querySelector('.ctm-search__form');
    this.input = this.querySelector('.ctm-search__input-field');
    this.searchBtn = this.querySelector('.ctm-search__input-button');
    this.keysWrap = this.querySelector('.ctm-search__keys');
    this.productsWrap = this.querySelector('.ctm-search__products');
    this.resultWrap = this.querySelector('.ctm-search__result');
    this.scrollWrapper = this.querySelector('.ctm-search__scroll');
    this.scrollItems = Array.from(this.querySelectorAll('.ctm-search__scroll__item'));
    this.isMobilePlacement = !!this.closest('.site-nav.burger-menu');
    this.isDesktopPlacement = !!this.closest('.ctm-header-search-wrap');
    this.isActiveInstance = false;

    if (!this.input || !this.form || !this.resultWrap) return;

    this.getRecentlyHandles();
    this.getRecentlyKeys();
    this.onViewportChange = this.syncInstanceState.bind(this);
    window.addEventListener('resize', this.onViewportChange);
    this.syncInstanceState();
    this.loadRecentlyKeys();
    this.loadRecentlyProduct();
    this.configResults('hide');
    this.hideResults();
  }

  shouldActivateInstance() {
    const isMobile = window.matchMedia('(max-width: 960px)').matches;
    if (this.isMobilePlacement) return isMobile;
    if (this.isDesktopPlacement) return !isMobile;
    return true;
  }

  syncInstanceState() {
    const shouldActivate = this.shouldActivateInstance();
    if (shouldActivate === this.isActiveInstance) return;
    this.isActiveInstance = shouldActivate;

    if (this.isActiveInstance) {
      this.style.display = '';
      this.bindEventListeners();
      this.configResults('hide');
      this.hideResults();
      return;
    }

    this.configResults('hide');
    this.hideResults();
    this.unbindEventListeners();
    this.style.display = 'none';
  }

  bindEventListeners() {
    this.onDocClick = this.handleDocumentClick.bind(this);
    this.onResultKeydown = this.handleResultKeydown.bind(this);
    this.onResetClick = this.handleResetBtnClick.bind(this);
    this.onFormSubmit = this.handleFormSubmit.bind(this);
    this.onSearchBtnClick = this.handleSearchBtnClick.bind(this);
    this.onInputFocus = this.handleInputSearch.bind(this);
    this.onInputSearch = CtmSearch.debounce(this.handleInputSearch.bind(this), 300);
    this.onInputKeydown = this.handleInputKeydown.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onMobileIconClick = () => {
      this.openMobileSearch();
      window.setTimeout(() => {
        if (!this.input) return;
        this.input.focus();
        this.input.click();
      }, 0);
    };
    this.onBackClick = () => this.closeMobileSearch({ restoreFocus: true });

    document.addEventListener('click', this.onDocClick);
    if (this.resultWrap) this.resultWrap.addEventListener('keydown', this.onResultKeydown);
    if (this.closeBtn) this.closeBtn.addEventListener('click', this.onResetClick);
    if (this.form) this.form.addEventListener('submit', this.onFormSubmit);
    if (this.searchBtn) this.searchBtn.addEventListener('click', this.onSearchBtnClick);
    if (this.input) {
      this.input.addEventListener('focus', this.onInputFocus);
      this.input.addEventListener('input', this.onInputSearch);
      this.input.addEventListener('keydown', this.onInputKeydown);
    }
    if (this.icon) this.icon.addEventListener('click', this.onMobileIconClick);
    if (this.backBtn) this.backBtn.addEventListener('click', this.onBackClick);
    this.addEventListener('focusout', this.onFocusOut);
  }

  unbindEventListeners() {
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
    if (this.resultWrap && this.onResultKeydown) this.resultWrap.removeEventListener('keydown', this.onResultKeydown);
    if (this.closeBtn && this.onResetClick) this.closeBtn.removeEventListener('click', this.onResetClick);
    if (this.form && this.onFormSubmit) this.form.removeEventListener('submit', this.onFormSubmit);
    if (this.searchBtn && this.onSearchBtnClick) this.searchBtn.removeEventListener('click', this.onSearchBtnClick);
    if (this.input && this.onInputFocus) this.input.removeEventListener('focus', this.onInputFocus);
    if (this.input && this.onInputSearch) this.input.removeEventListener('input', this.onInputSearch);
    if (this.input && this.onInputKeydown) this.input.removeEventListener('keydown', this.onInputKeydown);
    if (this.icon && this.onMobileIconClick) this.icon.removeEventListener('click', this.onMobileIconClick);
    if (this.backBtn && this.onBackClick) this.backBtn.removeEventListener('click', this.onBackClick);
    if (this.onFocusOut) this.removeEventListener('focusout', this.onFocusOut);
  }

  handleFocusOut(event) {
    const nextFocusedElement = event.relatedTarget;
    if (nextFocusedElement && this.contains(nextFocusedElement)) return;
    this.closeMobileSearch();
  }

  disconnectedCallback() {
    this.unbindEventListeners();
    if (this.onViewportChange) window.removeEventListener('resize', this.onViewportChange);
  }

  configResults(type = 'hide') {
    if (!this.searchInputWrap) return;
    if (type === 'show') {
      this.classList.add('is-open');
      this.searchInputWrap.classList.remove('ctm-search__input__hide');
      if (CtmSearch.getHeaderElement()?.dataset.overlay !== 'mobile-nav-open') {
        CtmSearch.setHeaderOverlayState('search-open');
      }
      return;
    }
    this.classList.remove('is-open');
    this.searchInputWrap.classList.add('ctm-search__input__hide');
    if (CtmSearch.getHeaderElement()?.dataset.overlay === 'search-open') {
      CtmSearch.setHeaderOverlayState(document.documentElement.classList.contains('ctm-mobile-nav-open') ? 'mobile-nav-open' : 'none');
    }
  }

  openMobileSearch() {
    this.configResults('show');
    this.showResults();
    if (this.input) this.input.focus();
  }

  closeMobileSearch({ restoreFocus = false } = {}) {
    this.configResults('hide');
    this.hideResults();
    if (restoreFocus && this.icon) this.icon.focus();
  }

  showResults() {
    if (this.resultWrap) this.resultWrap.style.display = 'block';
  }

  hideResults() {
    if (this.resultWrap) this.resultWrap.style.display = 'none';
  }

  clearProducts() {
    if (this.productsWrap) this.productsWrap.innerHTML = '';
  }

  triggerResultAction(target) {
    if (!target) return;
    const keyItem = target.closest('.ctm-search__result__key');
    if (keyItem && this.input) {
      this.input.value = keyItem.dataset.keyword || '';
      this.handleSearchKey();
      return;
    }

    const clearBtn = target.closest('.ctm-search__products__clear');
    if (clearBtn) {
      this.recentlyKeys = [];
      localStorage.setItem('ctm-search-keys', JSON.stringify(this.recentlyKeys));
      this.loadRecentlyKeys();
      this.loadRecentlyProduct();
    }
  }

  handleDocumentClick(event) {
    if (event.target.closest('ctm-search') === this) {
      this.triggerResultAction(event.target);
      return;
    }
    this.closeMobileSearch();
  }

  handleResultKeydown(event) {
    const isActionKey = event.key === 'Enter' || event.key === ' ';
    if (!isActionKey) return;
    const target = event.target.closest('.ctm-search__result__key, .ctm-search__products__clear');
    if (!target) return;
    event.preventDefault();
    this.triggerResultAction(target);
  }

  getRecentlyHandles() {
    let recent = {};

    // Keep data source aligned with legacy web project:
    // localStorage["theme-recent"] (shape: { [handle]: { ... } })
    try {
      const localRecent = window.localStorage.getItem('theme-recent');
      if (localRecent) {
        const parsed = JSON.parse(localRecent);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          recent = parsed;
        }
      }
    } catch (_err) {}

    // Backward-compatible fallback for themes that hydrate recentlyViewed on window.theme.
    if (!Object.keys(recent).length) {
      recent = window.theme && theme.recentlyViewed && theme.recentlyViewed.recent ? theme.recentlyViewed.recent : {};
    }

    const handles = [];
    for (const handle in recent) {
      handles.unshift(handle);
    }
    this.recentlyHandles = handles.slice(0, 6);
  }

  getRecentlyKeys() {
    const keys = JSON.parse(localStorage.getItem('ctm-search-keys') || '[]');
    this.recentlyKeys = Array.isArray(keys) ? keys : [];
  }

  setRecentlyKeys(key) {
    const keys = [...this.recentlyKeys];
    const keyIndex = keys.findIndex((item) => item === key);
    if (keyIndex !== -1) keys.splice(keyIndex, 1);
    this.recentlyKeys = [key, ...keys].slice(0, 12);
    localStorage.setItem('ctm-search-keys', JSON.stringify(this.recentlyKeys));
  }

  isDesktopScrollEnabled() {
    return window.matchMedia('(min-width: 769px)').matches;
  }

  getCurrentScrollKey() {
    if (!this.isDesktopScrollEnabled() || !this.scrollWrapper || !this.scrollItems.length) return '';
    const wrapperRect = this.scrollWrapper.getBoundingClientRect();
    const wrapperCenterY = wrapperRect.top + (wrapperRect.height / 2);
    let activeKey = '';
    let bestScore = -Infinity;

    for (const item of this.scrollItems) {
      const key = (item.textContent || '').trim();
      if (!key) continue;
      const style = window.getComputedStyle(item);
      const opacity = Number.parseFloat(style.opacity || '0') || 0;
      const rect = item.getBoundingClientRect();
      const centerY = rect.top + (rect.height / 2);
      const distance = Math.abs(centerY - wrapperCenterY);
      const score = (opacity * 1000) - distance;
      if (score > bestScore) {
        bestScore = score;
        activeKey = key;
      }
    }
    return activeKey;
  }

  getFallbackSearchKey() {
    if (this.isDesktopScrollEnabled()) {
      const activeKey = (this.getCurrentScrollKey() || '').trim();
      if (activeKey) return activeKey;
      const firstScrollKey = this.scrollItems
        .map((item) => (item.textContent || '').trim())
        .find((item) => item);
      if (firstScrollKey) return firstScrollKey;
    }
    return '';
  }

  handleSearchKey() {
    if (!this.input || !this.form) return;
    let value = this.input.value.trim();
    if (!value) {
      if (!this.isDesktopScrollEnabled()) return;
      value = this.getFallbackSearchKey();
      if (!value) return;
      this.input.value = value;
    }
    if (!value) return;
    this.setRecentlyKeys(value);
    this.form.submit();
    window.setTimeout(() => this.configResults('hide'), 300);
  }

  handleInputKeydown(event) {
    if (event.keyCode === 13 || event.key === 'Enter') {
      event.preventDefault();
      this.handleSearchKey();
    }
  }

  handleSearchBtnClick(event) {
    event.preventDefault();
    this.handleSearchKey();
  }

  handleFormSubmit(event) {
    event.preventDefault();
    this.handleSearchKey();
  }

  handleResetBtnClick() {
    if (!this.form || !this.input) return;
    this.form.reset();
    this.input.value = '';
    this.handleInputSearch();
  }

  createKeyResults({ title, keys }) {
    if (!this.keysWrap) return;
    let keyHtml = `${title}<ul>`;
    keys.forEach((key) => {
      keyHtml += `<li data-keyword="${CtmSearch.escapeHtml(key)}" class="ctm-search__result__key" role="button" tabindex="0" aria-label="Search for ${CtmSearch.escapeHtml(key)}">${CtmSearch.escapeHtml(key)}</li>`;
    });
    keyHtml += '</ul>';
    this.keysWrap.innerHTML = keyHtml;
  }

  createProductResults({ title, items = [] }) {
    if (!this.productsWrap) return;
    let productHtml = `${title}<ul class="ctm-search__products__list">`;
    items.forEach((product) => {
      const productUrl = CtmSearch.escapeHtml(product.url || '#');
      const productTitle = CtmSearch.escapeHtml(product.title || '');
      const imageSrc = (product.image || '').toString();
      const safeImage = imageSrc ? `${CtmSearch.escapeHtml(imageSrc)}${imageSrc.includes('?') ? '&' : '?'}width=160` : '';
      productHtml += `
        <li class="ctm-search__products__item">
          <a href="${productUrl}">
            ${safeImage ? `<img src="${safeImage}" loading="lazy" alt="${productTitle}">` : ''}
            <span>${productTitle}</span>
          </a>
        </li>
      `;
    });
    productHtml += '</ul>';

    let shouldUpdate = false;
    if (this.prevProducts.length !== items.length) shouldUpdate = true;
    if (!this.prevProducts.every((item) => items.some((product) => product.handle === item.handle))) {
      shouldUpdate = true;
    }
    if (shouldUpdate) this.productsWrap.innerHTML = productHtml;
    if (!items.length) this.productsWrap.innerHTML = '';
    this.prevProducts = items;
  }

  loadRecentlyKeys() {
    let keys = [...this.defaultKeywords];
    let titleHtml = '<div class="ctm-search__result__title">Popular Searches</div>';
    if (this.recentlyKeys.length > 0) {
      keys = [...this.recentlyKeys].slice(0, 6);
      titleHtml = `
        <div class="ctm-search__result__title">
          <span>Recent Searches</span>
          <span class="ctm-search__products__clear" role="button" tabindex="0" aria-label="Clear recent searches">Clear</span>
        </div>
      `;
    }
    this.createKeyResults({ title: titleHtml, keys });
  }

  createRecentlyProduct() {
    const items = [...this.recentlyHandles]
      .map((handle) => this.recentlyLists.get(handle))
      .filter(Boolean);
    this.createProductResults({
      title: '<div class="ctm-search__result__title">Recently Viewed</div>',
      items
    });
  }

  loadRecentlyProduct() {
    const promises = [];
    this.recentlyHandles.forEach((handle) => {
      if (this.recentlyLists.has(handle)) return;
      const fetchProduct = fetch(`/products/${handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .then((productData) => {
          if (!productData) return;
          this.recentlyLists.set(handle, {
            url: productData.url,
            title: productData.title,
            image: productData.featured_image,
            handle: productData.handle
          });
        })
        .catch(() => null);
      promises.push(fetchProduct);
    });
    Promise.allSettled(promises).then(() => this.createRecentlyProduct());
  }

  handleInputSearch() {
    this.configResults('show');
    this.showResults();
    const value = this.input ? this.input.value.trim() : '';
    if (!value) {
      this.loadRecentlyKeys();
      this.loadRecentlyProduct();
      return;
    }
    this.fetchSearchResults(value);
  }

  fetchSearchResults(value) {
    const searchTypesRaw = (this.dataset.searchTypes || 'product')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (!searchTypesRaw.includes('query')) searchTypesRaw.unshift('query');
    const searchTypes = Array.from(new Set(searchTypesRaw)).join(',');
    const params = new URLSearchParams({
      q: value,
      'resources[type]': searchTypes,
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'hide'
    });
    fetch(`/search/suggest.json?${params.toString()}`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!payload || !payload.resources || !payload.resources.results) return;
        const results = payload.resources.results;
        const products = results.products || [];
        const queries = results.queries || [];
        const keys = queries.length > 0 ? queries.map((query) => query.text) : [value];
        this.createKeyResults({
          title: '<div class="ctm-search__result__title">Search Suggestions</div>',
          keys
        });
        const items = products.map((product) => ({
          url: product.url,
          title: product.title,
          image: product.image || (product.featured_image && product.featured_image.url) || '',
          handle: product.handle
        }));
        this.createProductResults({
          title: '<div class="ctm-search__result__title">Product Suggestions</div>',
          items
        });
      })
      .catch(() => null);
  }
}
customElements.define('ctm-search', CtmSearch);

class CtmMobileNavA11y {
  constructor(host) {
    this.host = host;
    this.content = host.content;
    this.menuBtn = host.menuBtn;
    this.closeBtn = host.closeBtn;
    this.focusReturnEl = null;

    this.onEscapeKey = (event) => {
      if (!this.host.classList.contains('is-open') || event.key !== 'Escape') return;

      event.preventDefault();
      event.stopPropagation();
      this.host.close();
    };

    this.onDocumentKeyDown = (event) => {
      if (!this.host.classList.contains('is-open') || event.key !== 'Tab') return;

      const focusables = this.getFocusableElements();
      if (!focusables.length) {
        event.preventDefault();
        this.closeBtn?.focus();
        return;
      }

      const activeIndex = focusables.indexOf(document.activeElement);
      if (activeIndex === -1) {
        event.preventDefault();
        focusables[0].focus();
        return;
      }

      if (event.shiftKey && activeIndex === 0) {
        event.preventDefault();
        focusables[focusables.length - 1].focus();
      } else if (!event.shiftKey && activeIndex === focusables.length - 1) {
        event.preventDefault();
        focusables[0].focus();
      }
    };
  }

  connect() {
    window.addEventListener('keydown', this.onEscapeKey, true);
    window.addEventListener('keyup', this.onEscapeKey, true);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  disconnect() {
    window.removeEventListener('keydown', this.onEscapeKey, true);
    window.removeEventListener('keyup', this.onEscapeKey, true);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  captureFocusReturn() {
    this.focusReturnEl = document.activeElement instanceof HTMLElement ? document.activeElement : this.menuBtn;
  }

  setDrawerExpanded(expanded) {
    const value = expanded ? 'true' : 'false';
    this.menuBtn?.setAttribute('aria-expanded', value);
    this.closeBtn?.setAttribute('aria-expanded', value);
  }

  setTabActive(button, active) {
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  setPanelActive(panel, active) {
    panel.hidden = !active;
    panel.setAttribute('aria-hidden', active ? 'false' : 'true');
  }

  setSupportSectionExpanded(section, expanded) {
    const toggle = section.querySelector('.ctm-mobile-support-links__toggle');
    const list = section.querySelector('.ctm-mobile-support-links__list');
    if (!toggle || !list) return false;

    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    list.hidden = !expanded;
    list.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    return true;
  }

  getFocusableElements(container = this.content) {
    if (!container) return [];

    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const focusables = Array.from(container.querySelectorAll(selectors.join(','))).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.hasAttribute('hidden')) return false;
      if (element.closest('[hidden]')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      return element.offsetParent !== null;
    });

    if (this.closeBtn && !this.closeBtn.hasAttribute('hidden')) {
      const closeBtnStyle = window.getComputedStyle(this.closeBtn);
      if (closeBtnStyle.display !== 'none' && closeBtnStyle.visibility !== 'hidden') {
        focusables.push(this.closeBtn);
      }
    }

    return [...new Set(focusables)];
  }

  focusFirstVisibleElement() {
    const activePanel = this.host.querySelector('.ctm-mobile-nav-panel.is-active:not([hidden])');
    const focusables = this.getFocusableElements(activePanel || this.content);
    (focusables[0] || this.closeBtn)?.focus();
  }

  restoreFocus() {
    if (!this.focusReturnEl) return;

    const focusTarget = document.contains(this.focusReturnEl) ? this.focusReturnEl : this.menuBtn;
    focusTarget?.focus();
    this.focusReturnEl = null;
  }
}

class CtmMobileNav extends HTMLElement {
  static TRANSITION_MS = 450;

  static lockScroll(locked) {
    document.documentElement.classList.toggle('ctm-lock-scroll', locked);
  }

  static setHeaderOverlayState(overlay = 'none') {
    const headerEl = document.querySelector('#ctm-header');
    if (!headerEl) return;
    headerEl.dataset.overlay = overlay || 'none';
  }

  connectedCallback() {
    this.mask = this.querySelector('.ctm-mobile-nav__mask');
    this.menuBtn = document.querySelector('.ctm-nav-menu');
    this.closeBtn = document.querySelector('.ctm-nav-close');
    this.content = this.querySelector('.ctm-mobile-nav__content');
    this.innerCloseBtn = this.querySelector('.ctm-mobile-nav__panel-close');
    this.defaultTab = this.dataset.defaultTab || 'shop';
    this.tabBtns = Array.from(this.querySelectorAll('[data-mobile-tab-trigger]'));
    this.tabPanels = Array.from(this.querySelectorAll('.ctm-mobile-nav-panel'));
    this.supportLinks = Array.from(this.querySelectorAll('.ctm-mobile-support-links'));

    if (!this.mask || !this.menuBtn || !this.closeBtn || !this.content) return;

    this.a11y = new CtmMobileNavA11y(this);

    this.onMenuClick = () => this.open();
    this.onCloseClick = () => this.close();
    this.onMaskClick = () => this.close();
    this.onSupportToggleClick = (event) => {
      const toggle = event.target.closest('.ctm-mobile-support-links__toggle');
      if (!toggle || !this.contains(toggle)) return;
      event.preventDefault();
      const section = toggle.closest('.ctm-mobile-support-links');
      if (!section) return;
      this.toggleSupportSection(section);
    };
    this.onResize = () => {
      this.updateHeight();
      if (window.innerWidth > 960) this.close(true);
    };

    this.menuBtn.addEventListener('click', this.onMenuClick);
    this.closeBtn.addEventListener('click', this.onCloseClick);
    if (this.innerCloseBtn) this.innerCloseBtn.addEventListener('click', this.onCloseClick);
    this.mask.addEventListener('click', this.onMaskClick);
    this.addEventListener('click', this.onSupportToggleClick);
    window.addEventListener('resize', this.onResize);
    this.a11y.connect();

    this.initTabs();
    this.initSupportLinks();
    this.close(true);
  }

  disconnectedCallback() {
    if (this.menuBtn && this.onMenuClick) this.menuBtn.removeEventListener('click', this.onMenuClick);
    if (this.closeBtn && this.onCloseClick) this.closeBtn.removeEventListener('click', this.onCloseClick);
    if (this.innerCloseBtn && this.onCloseClick) this.innerCloseBtn.removeEventListener('click', this.onCloseClick);
    if (this.mask && this.onMaskClick) this.mask.removeEventListener('click', this.onMaskClick);
    if (this.onSupportToggleClick) this.removeEventListener('click', this.onSupportToggleClick);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.a11y?.disconnect();
    this.unbindTabs();
  }

  updateHeight() {
    if (window.innerWidth <= 960) {
      this.style.top = '0px';
      this.style.height = '100dvh';
      this.updateMobileStickyOffsets();
      return;
    }
    const header = document.querySelector('#ctm-header');
    const headerRect = header ? header.getBoundingClientRect() : null;
    const top = headerRect ? Math.max(headerRect.bottom, 0) : 0;
    this.style.top = `${top}px`;
    this.style.height = `calc(100vh - ${top}px)`;
  }

  updateMobileStickyOffsets() {
    const brand = this.querySelector('.ctm-mobile-nav__brand');
    if (!brand) return;
    const brandHeight = Math.round(brand.getBoundingClientRect().height);
    if (!brandHeight) return;
    this.style.setProperty('--ctm-mobile-brand-height', `${brandHeight}px`);
  }

  open() {
    if (window.innerWidth > 960) return;

    this.a11y?.captureFocusReturn();
    this.updateHeight();
    this.style.top = '0px';
    this.style.left = '0px';
    this.style.right = '0px';
    this.style.bottom = '0px';
    this.style.height = '100dvh';
    this.updateMobileStickyOffsets();
    this.classList.add('ctm-show');

    window.requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.a11y?.focusFirstVisibleElement();
    });

    this.menuBtn.classList.add('is-open');
    this.closeBtn.classList.add('is-open');
    this.a11y?.setDrawerExpanded(true);
    document.documentElement.classList.add('ctm-mobile-nav-open');
    CtmMobileNav.setHeaderOverlayState('mobile-nav-open');
    CtmMobileNav.lockScroll(true);
  }

  close(immediate = false) {
    this.classList.remove('is-open');
    this.closeBtn.classList.remove('is-open');
    this.menuBtn.classList.remove('is-open');
    this.a11y?.setDrawerExpanded(false);
    document.documentElement.classList.remove('ctm-mobile-nav-open');
    CtmMobileNav.setHeaderOverlayState(document.querySelector('ctm-search.is-open') ? 'search-open' : 'none');
    CtmMobileNav.lockScroll(false);

    const header = document.querySelector('#ctm-header');
    if (header && typeof header.updateTransparentState === 'function') {
      header.classList.remove('ctm-header--interactive');
      const scrollTop = Math.max(window.pageYOffset || document.documentElement.scrollTop || 0, 0);
      header.updateTransparentState(scrollTop);
    }

    this.dispatchEvent(new CustomEvent('ctm-mobile-nav:reset', { bubbles: true }));
    this.switchTab(this.defaultTab);

    if (immediate) {
      this.classList.remove('ctm-show');
      this.a11y?.restoreFocus();
      return;
    }

    window.setTimeout(() => {
      if (!this.classList.contains('is-open')) {
        this.classList.remove('ctm-show');
        this.a11y?.restoreFocus();
      }
    }, CtmMobileNav.TRANSITION_MS);
  }

  initTabs() {
    this.onTabClickHandlers = [];
    this.tabBtns.forEach((btn) => {
      const tab = btn.dataset.mobileTabTrigger;
      const handler = () => this.switchTab(tab);
      btn.addEventListener('click', handler);
      this.onTabClickHandlers.push({ btn, handler });
    });
    this.switchTab(this.defaultTab);
  }

  unbindTabs() {
    if (!this.onTabClickHandlers) return;
    this.onTabClickHandlers.forEach(({ btn, handler }) => btn.removeEventListener('click', handler));
    this.onTabClickHandlers = [];
  }

  initSupportLinks() {
    this.resetSupportLinks();
  }

  resetSupportLinks() {
    this.supportLinks.forEach((section) => {
      if (this.a11y?.setSupportSectionExpanded(section, false)) {
        section.classList.remove('is-open');
      }
    });
  }

  toggleSupportSection(section) {
    const isOpen = !section.classList.contains('is-open');
    if (this.a11y?.setSupportSectionExpanded(section, isOpen)) {
      section.classList.toggle('is-open', isOpen);
    }
  }

  switchTab(tabName) {
    const activeTab = tabName || 'shop';
    this.tabBtns.forEach((btn) => {
      const isActive = btn.dataset.mobileTabTrigger === activeTab;
      btn.classList.toggle('is-active', isActive);
      this.a11y?.setTabActive(btn, isActive);
    });

    this.tabPanels.forEach((panel) => {
      const panelTab = panel.dataset.mobileTab || 'shop';
      const isActive = panelTab === activeTab;
      panel.classList.toggle('is-active', isActive);
      this.a11y?.setPanelActive(panel, isActive);
    });

    this.resetSupportLinks();
  }
}
customElements.define('ctm-mobile-nav', CtmMobileNav);

class CtmMobileNavLink extends HTMLElement {
  static TRANSITION_MS = 450;

  connectedCallback() {
    this.mobileNav = this.closest('ctm-mobile-nav');
    this.titleEl = this.querySelector('.mobile-nav-link-title');
    this.titleBtn = this.querySelector('.mobile-nav-link-title-btn');
    this.contentEl = this.querySelector('.mobile-nav-link-content');
    this.backEl = this.querySelector('.mobile-nav-link-back');
    this.closeEl = this.querySelector('.mobile-nav-link-close');
    this.childToggles = Array.from(this.querySelectorAll('.mobile-nav-link-content-list-btn'));

    if (this.contentEl && this.titleBtn) {
      this.onTitleClick = () => this.openContent();
      this.titleBtn.addEventListener('click', this.onTitleClick);
    }

    if (this.backEl) {
      this.onBackClick = () => this.closeContent();
      this.backEl.addEventListener('click', this.onBackClick);
    }

    if (this.closeEl) {
      this.onCloseClick = () => {
        const mobileNav = this.closest('ctm-mobile-nav');
        if (mobileNav && typeof mobileNav.close === 'function') {
          mobileNav.close();
        }
      };
      this.closeEl.addEventListener('click', this.onCloseClick);
    }

    this.imageLoadListeners = [];

    this.childToggles.forEach((toggle) => {
      const parent = toggle.closest('.mobile-nav-link-childs');
      const panel = parent ? parent.querySelector('.mobile-nav-link-child') : null;
      if (!parent || !panel) return;

      toggle.addEventListener('click', () => {
        this.toggleChild(parent, panel, toggle);
      });

      const images = Array.from(panel.querySelectorAll('img'));
      images.forEach((img) => {
        const onImageLoad = () => this.syncOpenPanelHeight(parent, panel);
        if (!img.complete) img.addEventListener('load', onImageLoad);
        img.addEventListener('error', onImageLoad);
        this.imageLoadListeners.push({ img, onImageLoad });
      });
    });

    this.applyDefaultChildState();

    this.onNavReset = () => this.reset();
    this.addEventListener('ctm-mobile-nav:reset', this.onNavReset);
  }

  disconnectedCallback() {
    if (this.titleBtn && this.onTitleClick) this.titleBtn.removeEventListener('click', this.onTitleClick);
    if (this.backEl && this.onBackClick) this.backEl.removeEventListener('click', this.onBackClick);
    if (this.closeEl && this.onCloseClick) this.closeEl.removeEventListener('click', this.onCloseClick);
    if (this.onNavReset) this.removeEventListener('ctm-mobile-nav:reset', this.onNavReset);
    if (this.imageLoadListeners && this.imageLoadListeners.length) {
      this.imageLoadListeners.forEach(({ img, onImageLoad }) => {
        img.removeEventListener('load', onImageLoad);
        img.removeEventListener('error', onImageLoad);
      });
      this.imageLoadListeners = [];
    }
  }

  openContent() {
    if (!this.contentEl) return;
    this.classList.add('is-subpage-open');
    if (this.mobileNav) this.mobileNav.classList.add('has-subpage-open');
    if (this.titleBtn) this.titleBtn.setAttribute('aria-expanded', 'true');
    this.contentEl.hidden = false;
    this.contentEl.setAttribute('aria-hidden', 'false');
    this.contentEl.style.display = 'block';
    window.requestAnimationFrame(() => {
      this.contentEl.classList.add('is-open');
      this.applyDefaultChildState();
      this.focusFirstVisibleChild();
    });
  }

  closeContent() {
    if (!this.contentEl) return;
    this.contentEl.classList.remove('is-open');
    this.classList.remove('is-subpage-open');
    if (this.titleBtn) this.titleBtn.setAttribute('aria-expanded', 'false');
    this.contentEl.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      if (!this.contentEl.classList.contains('is-open')) {
        this.contentEl.style.display = 'none';
        this.contentEl.hidden = true;
      }
      if (this.mobileNav && !this.mobileNav.querySelector('.mobile-nav-link.is-subpage-open')) {
        this.mobileNav.classList.remove('has-subpage-open');
      }
      if (this.mobileNav && this.mobileNav.classList.contains('is-open') && this.titleBtn) {
        this.titleBtn.focus();
      }
    }, CtmMobileNavLink.TRANSITION_MS);
  }

  toggleChild(parent, panel, toggleButton) {
    const isOpen = parent.classList.contains('is-open');
    const layoutMode = panel.dataset.layout || 'list';
    const openDisplay = layoutMode === 'grid-2' ? 'grid' : 'block';
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }
    if (isOpen) {
      parent.classList.remove('is-open');
      if (panel.style.height === 'auto') {
        panel.style.height = `${panel.scrollHeight}px`;
      }
      window.requestAnimationFrame(() => {
        panel.style.height = '0px';
      });
      window.setTimeout(() => {
        if (!parent.classList.contains('is-open')) {
          panel.style.display = 'none';
          panel.hidden = true;
          panel.setAttribute('aria-hidden', 'true');
        }
      }, 300);
    } else {
      parent.classList.add('is-open');
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panel.style.display = openDisplay;
      panel.style.height = `${panel.scrollHeight}px`;
      window.setTimeout(() => this.syncOpenPanelHeight(parent, panel), 80);
      window.setTimeout(() => {
        if (parent.classList.contains('is-open')) {
          panel.style.height = 'auto';
        }
      }, 300);
    }
  }

  syncOpenPanelHeight(parent, panel) {
    if (!parent.classList.contains('is-open')) return;
    if (panel.style.display === 'none') return;
    if (panel.style.height === 'auto') return;
    panel.style.height = `${panel.scrollHeight}px`;
  }

  reset() {
    this.closeContent();
    this.applyDefaultChildState();
    if (this.mobileNav) this.mobileNav.classList.remove('has-subpage-open');
  }

  applyDefaultChildState() {
    this.querySelectorAll('.mobile-nav-link-childs').forEach((parent) => {
      const panel = parent.querySelector('.mobile-nav-link-child');
      const toggleButton = parent.querySelector('.mobile-nav-link-content-list-btn');
      if (!panel) return;
      const layoutMode = panel.dataset.layout || 'list';
      const openDisplay = layoutMode === 'grid-2' ? 'grid' : 'block';
      const shouldOpen = parent.dataset.defaultExpanded === 'true';

      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      }

      if (shouldOpen) {
        parent.classList.add('is-open');
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');
        panel.style.display = openDisplay;
        panel.style.height = 'auto';
      } else {
        parent.classList.remove('is-open');
        panel.style.height = '0px';
        panel.style.display = 'none';
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
      }
    });
  }

  focusFirstVisibleChild() {
    if (!this.contentEl) return;

    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const focusables = Array.from(this.contentEl.querySelectorAll(selectors.join(','))).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.hasAttribute('hidden')) return false;
      if (element.closest('[hidden]')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      return element.offsetParent !== null;
    });

    if (focusables.length) {
      focusables[0].focus();
    }
  }
}
customElements.define('ctm-mobile-nav-link', CtmMobileNavLink);

class CtmBeforeNavA11y {
  constructor(host) {
    this.host = host;
    this.bindings = [];
  }

  bindItem({ title, detail, key }) {
    const hasNativeTitleControl = !!title.querySelector('a[href], button:not([disabled])');
    if (!hasNativeTitleControl) {
      title.setAttribute('tabindex', '0');
      title.setAttribute('role', 'button');
      title.setAttribute('aria-expanded', 'false');
    }

    const onTitleKeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        if (hasNativeTitleControl) return;
        event.preventDefault();
        this.host.activate(key);
        return;
      }

      if (
        event.key !== 'Tab'
        || event.shiftKey
        || !detail.classList.contains('active')
      ) return;
      const firstDetailFocusable = this.getFocusableElements(detail)[0];
      if (!firstDetailFocusable) return;

      event.preventDefault();
      firstDetailFocusable.focus();
    };

    const onDetailKeydown = (event) => {
      if (event.key !== 'Tab' || !detail.classList.contains('active')) return;
      const detailFocusables = this.getFocusableElements(detail);
      if (!detailFocusables.length) return;

      if (event.shiftKey && event.target === detailFocusables[0]) {
        event.preventDefault();
        this.getTitleFocusTarget(title).focus();
        return;
      }

      if (!event.shiftKey && event.target === detailFocusables[detailFocusables.length - 1]) {
        const nextLeftFocusable = this.getNextLeftFocusable(title);
        if (!nextLeftFocusable) return;

        event.preventDefault();
        nextLeftFocusable.focus();
      }
    };

    title.addEventListener('keydown', onTitleKeydown);
    detail.addEventListener('keydown', onDetailKeydown);
    this.bindings.push({ title, detail, onTitleKeydown, onDetailKeydown });
  }

  getFocusableElements(container) {
    if (!container) return [];

    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => {
      if (element.hasAttribute('hidden') || element.closest('[hidden]')) return false;
      const styles = window.getComputedStyle(element);
      return styles.display !== 'none'
        && styles.visibility !== 'hidden'
        && (element.offsetWidth || element.offsetHeight || element.getClientRects().length);
    });
  }

  getTitleFocusTarget(title) {
    return title.querySelector('a[href], button:not([disabled])') || title;
  }

  getNextLeftFocusable(title) {
    const leftFocusables = this.getFocusableElements(this.host.left);
    const currentIndex = leftFocusables.indexOf(this.getTitleFocusTarget(title));
    return currentIndex === -1 ? null : leftFocusables[currentIndex + 1] || null;
  }

  destroy() {
    this.bindings.forEach(({ title, detail, onTitleKeydown, onDetailKeydown }) => {
      title.removeEventListener('keydown', onTitleKeydown);
      detail.removeEventListener('keydown', onDetailKeydown);
    });
    this.bindings = [];
  }
}

class CtmBeforeNav extends HTMLElement {
  connectedCallback() {
    this.left = this.querySelector('.ctm-before-nav-left');
    this.right = this.querySelector('.ctm-before-nav-right');
    if (!this.left || !this.right) return;

    this.items = Array.from(this.left.querySelectorAll('.ctm-before-nav-item'));
    this.map = new Map();
    this.a11y = new CtmBeforeNavA11y(this);
    this.syncLeftHeight = () => this.updateLeftHeight();

    this.items.forEach((item) => {
      const title = item.querySelector('.ctm-before-nav-item-title');
      const detail = item.querySelector('.ctm-before-nav-item-detail');
      const key = title && title.dataset ? title.dataset.title : '';
      if (!title || !detail || !key) return;

      const isRichTextDetail = detail.querySelector('.ctm-before-nav-item-text, .ctm-before-nav-item-image')
        && !detail.querySelector('.ctm-pc-nav__shades-items, .ctm-nav__lookbook, .ctm-pc-nav__menu');
      if (isRichTextDetail) {
        title.classList.remove('has-detail-arrow');
        const arrow = title.querySelector('.ctm-before-nav-item-arrow');
        if (arrow) arrow.remove();
      }

      this.right.appendChild(detail);
      this.map.set(key, { title, detail });
      this.a11y.bindItem({ title, detail, key });

      title.addEventListener('mouseenter', () => this.activate(key));
      title.addEventListener('focusin', () => this.activate(key));
    });

    // this.addEventListener('mouseleave', () => this.reset());
    this.updateLeftHeight();
    if ('ResizeObserver' in window) {
      this.leftResizeObserver = new ResizeObserver(this.syncLeftHeight);
      this.leftResizeObserver.observe(this.left);
    } else {
      window.addEventListener('resize', this.syncLeftHeight);
    }

    const firstKey = this.map.keys().next();
    if (!firstKey.done) this.activate(firstKey.value);
  }

  updateLeftHeight() {
    if (!this.left) return;
    this.style.setProperty('--ctm-before-nav-left-height', `${this.left.offsetHeight}px`);
  }

  activate(key) {
    this.map.forEach((item, itemKey) => {
      const active = itemKey === key;
      item.title.classList.toggle('active', active);
      if (item.title.getAttribute('role') === 'button') {
        item.title.setAttribute('aria-expanded', active ? 'true' : 'false');
      }
      item.detail.classList.toggle('active', active);
    });
  }

  reset() {
    this.map.forEach((item) => {
      item.title.classList.remove('active');
      if (item.title.getAttribute('role') === 'button') {
        item.title.setAttribute('aria-expanded', 'false');
      }
      item.detail.classList.remove('active');
    });
  }

  disconnectedCallback() {
    if (this.leftResizeObserver) {
      this.leftResizeObserver.disconnect();
      this.leftResizeObserver = null;
    } else if (this.syncLeftHeight) {
      window.removeEventListener('resize', this.syncLeftHeight);
    }
    this.a11y?.destroy();
    this.a11y = null;
  }
}
customElements.define('ctm-before-nav', CtmBeforeNav);

class CtmHeaderContainer extends HTMLElement {
  connectedCallback() {
    const navItems = this.querySelectorAll('.ctm-pc-nav__content-item');
    navItems.forEach((item) => {
      const link = item.querySelector('.ctm-pc-nav__content-link[aria-haspopup="true"]');
      if (!link) return;

      const setExpanded = (expanded) => link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      const closeIfFocusLeavesItem = (event) => {
        const nextFocused = event.relatedTarget;
        if (nextFocused instanceof Node && item.contains(nextFocused)) return;

        window.requestAnimationFrame(() => {
          const activeEl = document.activeElement;
          if (!activeEl || !item.contains(activeEl)) {
            setExpanded(false);
          }
        });
      };

      item.addEventListener('mouseenter', () => setExpanded(true));
      item.addEventListener('mouseleave', () => setExpanded(false));
      item.addEventListener('focusin', () => setExpanded(true));
      item.addEventListener('focusout', closeIfFocusLeavesItem);
    });

    this.sectionEl = this.closest('.shopify-section');
    this.transparentEnabled = this.dataset.transparentEnabled === 'true';
    this.transparentScheme = this.dataset.transparentScheme || 'default';
    this.scrollThreshold = 8;
    this.minHideScrollTop = 64;
    this.directionDelta = 6;
    this.lastScrollTop = Math.max(window.pageYOffset || document.documentElement.scrollTop || 0, 0);

    if (this.transparentEnabled) {
      if (this.sectionEl) this.sectionEl.classList.add('ctm-header-section--transparent-enabled');
      document.body.classList.add('ctm-header-transparent-enabled');
      document.body.classList.toggle('ctm-header-transparent-inverse', this.transparentScheme === 'inverse');
      this.updateTransparentState(this.lastScrollTop);
      this.bindTransparentInteraction();
      this.syncInitialTransparentInteraction();
      
    }
    if (!this.dataset.overlay) this.dataset.overlay = 'none';
    this.updateLeftLinksActiveState();

    this.onWindowScroll = () => this.handleWindowScroll();
    this.onWindowResize = () => {
      this.showHeader();
      this.lastScrollTop = Math.max(window.pageYOffset || document.documentElement.scrollTop || 0, 0);
      if (this.transparentEnabled) this.updateTransparentState(this.lastScrollTop);
    };

    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    window.addEventListener('resize', this.onWindowResize);
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      const sections = event?.cart?.sections;
      this.updateItemNum(sections);
    });
  }

  updateItemNum(sections = {}) {
    if (sections['cart-items-num']) {
      const html = sections['cart-items-num'];
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.cart-items-num');
      const cartItemsNum = document.querySelector('ctm-header-container .cart-link .cart-items-num');
      if (!content || !cartItemsNum) return;
      cartItemsNum.outerHTML = content.outerHTML;
    }
  }

  updateTransparentState(scrollTopValue) {
    if (!this.transparentEnabled) return;
    const scrollTop = typeof scrollTopValue === 'number'
      ? scrollTopValue
      : (window.pageYOffset || document.documentElement.scrollTop || 0);
    const isScrolled = scrollTop > this.scrollThreshold;
    this.classList.toggle('ctm-header--scrolled', isScrolled);
    this.classList.toggle('ctm-header--transparent-top', !isScrolled);
    if (isScrolled) this.classList.remove('ctm-header--interactive');
    if (!isScrolled && window.innerWidth <= 960) this.classList.remove('ctm-header--interactive');
    document.body.classList.toggle('ctm-header-transparent-scrolled', isScrolled);
  }

  bindTransparentInteraction() {
    this.interactionTarget = this.sectionEl || this;
    this.onHeaderMouseEnter = () => {
      if (window.innerWidth > 960 && this.classList.contains('ctm-header--transparent-top')) {
        this.classList.add('ctm-header--interactive');
      }
    };
    this.onHeaderMouseLeave = () => {
      this.classList.remove('ctm-header--interactive');
    };
    this.onHeaderFocusIn = () => {
      if (!(window.innerWidth > 960 && this.classList.contains('ctm-header--transparent-top'))) return;
      const focusTarget = document.activeElement;
      const isFocusVisible = focusTarget && typeof focusTarget.matches === 'function'
        ? focusTarget.matches(':focus-visible')
        : false;
      if (isFocusVisible) this.classList.add('ctm-header--interactive');
    };
    this.onHeaderFocusOut = () => {
      if (!this.interactionTarget) return;
      const nextFocused = document.activeElement;
      if (!nextFocused || !this.interactionTarget.contains(nextFocused)) {
        if (window.innerWidth > 960 && this.classList.contains('ctm-header--transparent-top') && this.interactionTarget.matches(':hover')) {
          return;
        }
        this.classList.remove('ctm-header--interactive');
      }
    };

    this.interactionTarget.addEventListener('mouseenter', this.onHeaderMouseEnter);
    this.interactionTarget.addEventListener('mouseleave', this.onHeaderMouseLeave);
    this.interactionTarget.addEventListener('focusin', this.onHeaderFocusIn);
    this.interactionTarget.addEventListener('focusout', this.onHeaderFocusOut);
  }

  syncInitialTransparentInteraction() {
    if (!this.transparentEnabled) return;
    if (!(window.innerWidth > 960 && this.classList.contains('ctm-header--transparent-top'))) return;
    if (!this.interactionTarget || typeof this.interactionTarget.matches !== 'function') return;
    if (this.interactionTarget.matches(':hover')) {
      this.classList.add('ctm-header--interactive');
    }
  }

  updateLeftLinksActiveState() {
    const links = Array.from(this.querySelectorAll('.ctm-header-left-link[href]'));
    if (!links.length) return;

    const normalizePath = (value) => {
      if (!value) return '';
      const trimmed = value.split('?')[0].split('#')[0];
      const withoutTrailing = trimmed.replace(/\/+$/, '');
      return withoutTrailing || '/';
    };

    const currentPath = normalizePath(window.location.pathname || '/');
    links.forEach((link) => {
      const rawHref = link.getAttribute('href') || '';
      if (!rawHref || rawHref === '#' || rawHref.startsWith('javascript:')) {
        link.classList.remove('is-active');
        link.removeAttribute('aria-current');
        return;
      }

      let linkPath = '';
      try {
        linkPath = normalizePath(new URL(rawHref, window.location.origin).pathname);
      } catch (error) {
        link.classList.remove('is-active');
        link.removeAttribute('aria-current');
        return;
      }

      const isActive = linkPath !== '/' && (currentPath === linkPath || currentPath.endsWith(linkPath));
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  hideHeader() {
    if (document.documentElement.classList.contains('ctm-mobile-nav-open')) return;
    if (this.sectionEl) this.sectionEl.classList.add('header-hidden');
  }

  showHeader() {
    if (this.sectionEl) this.sectionEl.classList.remove('header-hidden');
  }

  handleWindowScroll() {
    const scrollTop = Math.max(window.pageYOffset || document.documentElement.scrollTop || 0, 0);
    if (this.transparentEnabled) this.updateTransparentState(scrollTop);

    if (document.documentElement.classList.contains('ctm-mobile-nav-open')) {
      this.showHeader();
      this.lastScrollTop = scrollTop;
      return;
    }

    if (window.innerWidth < 961) {
      this.showHeader();
      this.lastScrollTop = scrollTop;
      return;
    }

    if (scrollTop <= this.minHideScrollTop) {
      this.showHeader();
      this.lastScrollTop = scrollTop;
      return;
    }

    const delta = scrollTop - this.lastScrollTop;
    if (Math.abs(delta) < this.directionDelta) return;

    if (delta > 0) {
      this.hideHeader();
    } else {
      this.showHeader();
    }

    this.lastScrollTop = scrollTop;
  }

  disconnectedCallback() {
    if (this.onWindowScroll) window.removeEventListener('scroll', this.onWindowScroll);
    if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }
    if (this.interactionTarget) {
      if (this.onHeaderMouseEnter) this.interactionTarget.removeEventListener('mouseenter', this.onHeaderMouseEnter);
      if (this.onHeaderMouseLeave) this.interactionTarget.removeEventListener('mouseleave', this.onHeaderMouseLeave);
      if (this.onHeaderFocusIn) this.interactionTarget.removeEventListener('focusin', this.onHeaderFocusIn);
      if (this.onHeaderFocusOut) this.interactionTarget.removeEventListener('focusout', this.onHeaderFocusOut);
    }
    this.showHeader();

    if (this.transparentEnabled) {
      this.classList.remove('ctm-header--interactive', 'ctm-header--transparent-top');
      if (this.sectionEl) this.sectionEl.classList.remove('ctm-header-section--transparent-enabled');
      document.body.classList.remove(
        'ctm-header-transparent-enabled',
        'ctm-header-transparent-inverse',
        'ctm-header-transparent-scrolled'
      );
    }
    delete this.dataset.overlay;
  }
}
customElements.define('ctm-header-container', CtmHeaderContainer);

class CtmContactUsPopup extends HTMLElement {
  openLiveChat() {
    if (window.innerWidth < 768) {
      if (window.LC_API && typeof window.LC_API.open_mobile_window === 'function') {
        window.LC_API.open_mobile_window();
      }
      return;
    }

    if (window.LC_API && typeof window.LC_API.open_chat_window === 'function') {
      window.LC_API.open_chat_window();
    }
  }

  connectedCallback() {
    this.querySelectorAll('.chat-now').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.openLiveChat();
      });
    });

    this.serviceMenu = this.querySelector('.ctm-service-menu');
    this.serviceTrigger = this.querySelector('.ctm-service-trigger');
    this.servicePopover = this.querySelector('.ctm-service-popover');
    if (!this.serviceMenu || !this.serviceTrigger || !this.servicePopover) return;

    this.servicePopover.hidden = true;
    this.servicePopover.setAttribute('aria-hidden', 'true');
    this.servicePopover.setAttribute('role', 'region');
    const popoverTitle = this.servicePopover.querySelector('.ctm-service-help__title');
    if (popoverTitle && popoverTitle.id) {
      this.servicePopover.setAttribute('aria-labelledby', popoverTitle.id);
    }

    this.openServicePopover = () => {
      this.serviceMenu.classList.add('is-open');
      this.serviceTrigger.setAttribute('aria-expanded', 'true');
      this.servicePopover.hidden = false;
      this.servicePopover.setAttribute('aria-hidden', 'false');
    };

    this.closeServicePopover = () => {
      this.serviceMenu.classList.remove('is-open');
      this.serviceTrigger.setAttribute('aria-expanded', 'false');
      this.servicePopover.hidden = true;
      this.servicePopover.setAttribute('aria-hidden', 'true');
    };

    this.toggleServicePopover = () => {
      const isOpen = this.serviceMenu.classList.contains('is-open');
      if (isOpen) {
        this.closeServicePopover();
      } else {
        this.openServicePopover();
      }
    };

    this.onServiceTriggerClick = (event) => {
      event.preventDefault();
      this.toggleServicePopover();
    };
    this.onServiceMenuMouseEnter = () => this.openServicePopover();
    this.onServiceMenuFocusIn = () => this.openServicePopover();
    this.onServiceMenuMouseLeave = () => this.closeServicePopover();
    this.onServiceMenuFocusOut = (event) => {
      const nextFocused = event.relatedTarget;
      if (nextFocused && this.serviceMenu.contains(nextFocused)) return;
      this.closeServicePopover();
    };
    this.onDocumentClickForService = (event) => {
      if (!this.serviceMenu.contains(event.target)) this.closeServicePopover();
    };
    this.onDocumentKeyDownForService = (event) => {
      if (event.key !== 'Escape') return;
      this.closeServicePopover();
      this.serviceTrigger.focus();
    };

    this.serviceTrigger.addEventListener('click', this.onServiceTriggerClick);
    this.serviceMenu.addEventListener('mouseenter', this.onServiceMenuMouseEnter);
    this.serviceMenu.addEventListener('focusin', this.onServiceMenuFocusIn);
    this.serviceMenu.addEventListener('mouseleave', this.onServiceMenuMouseLeave);
    this.serviceMenu.addEventListener('focusout', this.onServiceMenuFocusOut);
    document.addEventListener('click', this.onDocumentClickForService);
    document.addEventListener('keydown', this.onDocumentKeyDownForService);
  }

  disconnectedCallback() {
    if (this.serviceTrigger && this.onServiceTriggerClick) {
      this.serviceTrigger.removeEventListener('click', this.onServiceTriggerClick);
    }
    if (this.serviceMenu && this.onServiceMenuMouseEnter) {
      this.serviceMenu.removeEventListener('mouseenter', this.onServiceMenuMouseEnter);
    }
    if (this.serviceMenu && this.onServiceMenuFocusIn) {
      this.serviceMenu.removeEventListener('focusin', this.onServiceMenuFocusIn);
    }
    if (this.serviceMenu && this.onServiceMenuMouseLeave) {
      this.serviceMenu.removeEventListener('mouseleave', this.onServiceMenuMouseLeave);
    }
    if (this.serviceMenu && this.onServiceMenuFocusOut) {
      this.serviceMenu.removeEventListener('focusout', this.onServiceMenuFocusOut);
    }
    if (this.onDocumentClickForService) {
      document.removeEventListener('click', this.onDocumentClickForService);
    }
    if (this.onDocumentKeyDownForService) {
      document.removeEventListener('keydown', this.onDocumentKeyDownForService);
    }
  }
}
customElements.define('ctm-contact-us-popup', CtmContactUsPopup);

class CtmCountdown extends HTMLElement {
  constructor() {
    super();
    this.countdownExpired = false;
    this.countdownInterval = null;
  }

  connectedCallback() {
    const targetDate = this.getAttribute('target-date');
    if (!targetDate || Number.isNaN(new Date(targetDate).getTime())) {
      console.error('CtmCountdown: Invalid target date format. Please use YYYY-MM-DDTHH:MM:SS format.');
      return;
    }
    this.initFetchTime(targetDate);
  }

  switchToAnnouncementBar() {
    const countdownContainer = document.getElementById('countdown-container');
    const announcementBar = document.getElementById('ctm-announcement-bar');
    const announcementContainer = document.getElementById('ctm-announcement-container');
    if (!announcementBar) return;
    if (countdownContainer) countdownContainer.style.display = 'none';
    announcementBar.style.cssText = 'display: flex !important';
    this.countdownExpired = true;

    requestAnimationFrame(() => {
      const announcementHeight = announcementContainer ? announcementContainer.getBoundingClientRect().height : 21;
      document.documentElement.style.setProperty('--header-announcement-height', `${announcementHeight + 20}px`);
      this.startAnnouncementBarSlide();
    });
  }

  initFetchTime(targetDate) {
    const apiBaseUrl = FoxTheme.ctm.apiBaseUrl;

    fetch(`${apiBaseUrl}/vote/server-time`, {
      method: 'POST',
      body: JSON.stringify({ timeZone: 'Australia/Sydney' }),
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Server responded with status ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (data.code !== 200) {
          throw new Error(`Server error: ${data.message || 'Unknown error'}`);
        }

        const announcementBar = document.getElementById('ctm-announcement-bar');
        const serverTime = this.parseServerTime(data.data);
        const endTime = new Date(targetDate).getTime();

        if (serverTime >= endTime) {
          this.switchToAnnouncementBar();
          return;
        }

        if (announcementBar) announcementBar.style.cssText = 'display: none !important';
        this.initCountdown(serverTime, endTime);
      })
      .catch((error) => {
        console.warn('CtmCountdown: Server time fetch failed, falling back to local time.', error);
        this.fallbackToLocalTime(targetDate);
      });
  }

  fallbackToLocalTime(targetDate) {
    const announcementBar = document.getElementById('ctm-announcement-bar');
    const endTime = new Date(targetDate).getTime();
    const serverTime = Date.now();
    if (serverTime >= endTime) {
      this.switchToAnnouncementBar();
      return;
    }
    if (announcementBar) announcementBar.style.cssText = 'display: none !important';
    this.initCountdown(serverTime, endTime);
  }

  initCountdown(serverTime, endTime) {
    const timeOffset = serverTime - Date.now();
    const updateCountdown = () => {
      const timeLeft = endTime - (Date.now() + timeOffset);
      if (timeLeft <= 0) {
        this.setCountdownValues(0, 0, 0, 0);
        clearInterval(this.countdownInterval);
        this.switchToAnnouncementBar();
        return;
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      this.setCountdownValues(days, hours, minutes, seconds);
    };

    updateCountdown();
    this.countdownInterval = window.setInterval(updateCountdown, 1000);
  }

  setCountdownValues(days, hours, minutes, seconds) {
    const pad = (n) => n.toString().padStart(2, '0');
    const daysEl = this.querySelector('#days');
    const hoursEl = this.querySelector('#hours');
    const minutesEl = this.querySelector('#minutes');
    const secondsEl = this.querySelector('#seconds');
    if (!daysEl || !hoursEl || !minutesEl || !secondsEl) return;

    daysEl.innerText = pad(days);
    hoursEl.innerText = pad(hours);
    minutesEl.innerText = pad(minutes);
    secondsEl.innerText = pad(seconds);
  }

  parseServerTime(serverTimeData) {
    return new Date(serverTimeData && serverTimeData.localTime ? serverTimeData.localTime : '').getTime();
  }

  startAnnouncementBarSlide() {
    const slider = document.querySelector('.announcement-slider');
    if (!slider) return;

    if (slider.swiper) {
      slider.swiper.update();
      slider.swiper.slideToLoop(0, 0);
      slider.swiper.autoplay.start();
      return;
    }

    const instance = new window.FoxTheme.Carousel(
      slider,
      {
        loop: true,
        slidesPerView: 1,
        allowTouchMove: true,
        autoplay: { delay: 4000, disableOnInteraction: false }
      },
      [window.FoxTheme.Swiper.Autoplay]
    );
    instance.init();
  }

  disconnectedCallback() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  }
}
customElements.define('ctm-countdown', CtmCountdown);

class CtmAnnouncementContainer extends HTMLElement {
  constructor() {
    super();
    this.lastScrollTop = 0;
    this.ticking = false;
  }

  connectedCallback() {
    const sectionEl = this.closest('.shopify-section-group-header-group');
    if (sectionEl) {
      sectionEl.classList.add('ctm-announcement-section');
    }

    this.showAnnouncement = this.getAttribute('data-show-announcement') === 'true';
    this.hasCountdown = this.getAttribute('data-has-countdown') === 'true';
    this._scrollHandler = () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };
    window.addEventListener('scroll', this._scrollHandler);
    this.initDisplayState();
    this.initServiceMenuA11y();
    this.initMobileMenuStickyHeight();
  }

  initServiceMenuA11y() {
    this.serviceMenu = this.querySelector('.ctm-service-menu');
    this.serviceTrigger = this.querySelector('.ctm-service-trigger');
    this.servicePopover = this.querySelector('.ctm-service-popover');
    if (!this.serviceMenu || !this.serviceTrigger || !this.servicePopover) return;

    this.servicePopover.hidden = true;
    this.servicePopover.setAttribute('aria-hidden', 'true');
    this.servicePopover.setAttribute('role', 'region');
    const popoverTitle = this.servicePopover.querySelector('.ctm-service-help__title');
    if (popoverTitle && popoverTitle.id) {
      this.servicePopover.setAttribute('aria-labelledby', popoverTitle.id);
    }

    this.openServicePopover = () => {
      this.serviceMenu.classList.add('is-open');
      this.serviceTrigger.setAttribute('aria-expanded', 'true');
      this.servicePopover.hidden = false;
      this.servicePopover.setAttribute('aria-hidden', 'false');
    };

    this.closeServicePopover = () => {
      this.serviceMenu.classList.remove('is-open');
      this.serviceTrigger.setAttribute('aria-expanded', 'false');
      this.servicePopover.hidden = true;
      this.servicePopover.setAttribute('aria-hidden', 'true');
    };

    this.toggleServicePopover = () => {
      const isOpen = this.serviceMenu.classList.contains('is-open');
      if (isOpen) {
        this.closeServicePopover();
      } else {
        this.openServicePopover();
      }
    };

    this.onServiceTriggerClick = (event) => {
      event.preventDefault();
      this.toggleServicePopover();
    };
    this.onServiceMenuMouseEnter = () => this.openServicePopover();
    this.onServiceMenuFocusIn = () => this.openServicePopover();
    this.onServiceMenuMouseLeave = () => this.closeServicePopover();
    this.onServiceMenuFocusOut = (event) => {
      const nextFocused = event.relatedTarget;
      if (nextFocused && this.serviceMenu.contains(nextFocused)) return;
      this.closeServicePopover();
    };
    this.onDocumentClickForService = (event) => {
      if (!this.serviceMenu.contains(event.target)) this.closeServicePopover();
    };
    this.onDocumentKeyDownForService = (event) => {
      if (event.key !== 'Escape') return;
      this.closeServicePopover();
      this.serviceTrigger.focus();
    };

    this.serviceTrigger.addEventListener('click', this.onServiceTriggerClick);
    this.serviceMenu.addEventListener('mouseenter', this.onServiceMenuMouseEnter);
    this.serviceMenu.addEventListener('focusin', this.onServiceMenuFocusIn);
    this.serviceMenu.addEventListener('mouseleave', this.onServiceMenuMouseLeave);
    this.serviceMenu.addEventListener('focusout', this.onServiceMenuFocusOut);
    document.addEventListener('click', this.onDocumentClickForService);
    document.addEventListener('keydown', this.onDocumentKeyDownForService);
  }

  initDisplayState() {
    if (!this.showAnnouncement || this.hasCountdown) return;
    const sliderEl = this.querySelector('.announcement-slider');
    if (!sliderEl) return;
    const slides = Array.from(sliderEl.querySelectorAll('.swiper-slide'));
    if (slides.length <= 1) return;

    this.swiperInstance = new window.FoxTheme.Carousel(
      sliderEl,
      {
        loop: true,
        slidesPerView: 1,
        allowTouchMove: true,
        autoplay: { delay: 4000, pauseOnMouseEnter: true, disableOnInteraction: false }
      },
      [window.FoxTheme.Swiper.Autoplay]
    );
    this.swiperInstance.init();
    this.sliderEl = sliderEl;
    this.swiperSlider = this.swiperInstance && this.swiperInstance.slider;
    this.isAnnouncementPaused = false;
    this.bindAnnouncementAccessibilityEvents();
    this.updateAnnouncementSlideA11y();
  }

  bindAnnouncementAccessibilityEvents() {
    if (!this.swiperSlider || !this.sliderEl) return;
    this.onAnnouncementSlideChange = () => this.updateAnnouncementSlideA11y();
    this.swiperSlider.on('slideChange', this.onAnnouncementSlideChange);
    this.swiperSlider.on('activeIndexChange', this.onAnnouncementSlideChange);
    this.swiperSlider.on('slideChangeTransitionEnd', this.onAnnouncementSlideChange);
    this.swiperSlider.on('afterInit', this.onAnnouncementSlideChange);
    this.swiperSlider.on('loopFix', this.onAnnouncementSlideChange);
    this.announcementSlideObserver = new MutationObserver((mutations) => {
      const hasSlideClassChange = mutations.some((mutation) => {
        const target = mutation.target;
        return (
          mutation.type === 'attributes'
          && mutation.attributeName === 'class'
          && target
          && target.classList
          && target.classList.contains('swiper-slide')
        );
      });
      if (hasSlideClassChange) this.updateAnnouncementSlideA11y();
    });
    this.announcementSlideObserver.observe(this.sliderEl, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    this.onAnnouncementFocusIn = () => this.pauseAnnouncementAutoplay();
    this.onAnnouncementFocusOut = (event) => {
      const nextFocused = event.relatedTarget;
      if (nextFocused && this.sliderEl.contains(nextFocused)) return;
      this.resumeAnnouncementAutoplay();
    };
    this.sliderEl.addEventListener('focusin', this.onAnnouncementFocusIn);
    this.sliderEl.addEventListener('focusout', this.onAnnouncementFocusOut);
  }

  updateAnnouncementSlideA11y() {
    if (!this.swiperSlider || !this.sliderEl) return;
    const allSlides = Array.from(this.sliderEl.querySelectorAll('.swiper-slide'));
    let activeSlides = allSlides.filter((slide) => slide.classList.contains('swiper-slide-active'));
    if (!activeSlides.length && Number.isInteger(this.swiperSlider.activeIndex)) {
      const fallbackActive = allSlides[this.swiperSlider.activeIndex];
      activeSlides = fallbackActive ? [fallbackActive] : [];
    }
    const activeSet = new Set(activeSlides);

    allSlides.forEach((slide, index) => {
      const isActive = activeSet.has(slide);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      const focusables = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]');
      focusables.forEach((el) => {
        if (isActive) {
          if (el.hasAttribute('data-orig-tabindex')) {
            const original = el.getAttribute('data-orig-tabindex');
            if (original === '') {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', original);
            }
            el.removeAttribute('data-orig-tabindex');
          } else if (el.getAttribute('tabindex') === '-1' && !el.hasAttribute('data-orig-tabindex')) {
            el.removeAttribute('tabindex');
          }
        } else {
          if (!el.hasAttribute('data-orig-tabindex')) {
            el.setAttribute('data-orig-tabindex', el.getAttribute('tabindex') || '');
          }
          el.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  pauseAnnouncementAutoplay() {
    if (!this.swiperSlider || !this.swiperSlider.autoplay) return;
    this.swiperSlider.autoplay.stop();
    this.isAnnouncementPaused = true;
  }

  resumeAnnouncementAutoplay() {
    if (!this.swiperSlider || !this.swiperSlider.autoplay) return;
    this.swiperSlider.autoplay.start();
    this.isAnnouncementPaused = false;
  }

  getHeaderHeight() {
    const headerGroup = document.querySelector('.ctm-custom-header_bar');
    return headerGroup ? headerGroup.getBoundingClientRect().height : 0;
  }

  initMobileMenuStickyHeight() {
    if (window.innerWidth > 960) return;
    const headerHeight = this.getHeaderHeight();
    const announcementHeight = this.getBoundingClientRect().height;
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (currentScrollTop > 0) {
      document.documentElement.style.setProperty('--sticky-header-height-mobile', `${headerHeight}px`);
    } else {
      document.documentElement.style.setProperty('--sticky-header-height-mobile', `${headerHeight + announcementHeight}px`);
    }
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDirection = scrollTop > this.lastScrollTop ? 'down' : 'up';
    const scrollDistance = Math.abs(scrollTop - this.lastScrollTop);
    this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    if (scrollDistance <= 5) return;
    this.updateStickyHeaderHeight(scrollDirection, scrollTop);
  }

  updateStickyHeaderHeight(scrollDirection, scrollTop) {
    const headerHeight = this.getHeaderHeight();
    const announcementHeight = this.getBoundingClientRect().height;
    if (scrollTop < announcementHeight && window.innerWidth < 961) {
      document.documentElement.style.setProperty('--sticky-header-height-mobile', `${headerHeight + announcementHeight - scrollTop}px`);
    } else if (window.innerWidth > 960) {
      const val = scrollDirection === 'down' ? '0px' : `${headerHeight}px`;
      document.documentElement.style.setProperty('--sticky-header-height-pc', val);
    } else {
      document.documentElement.style.setProperty('--sticky-header-height-mobile', `${headerHeight}px`);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._scrollHandler);
    if (this.sliderEl && this.onAnnouncementFocusIn) {
      this.sliderEl.removeEventListener('focusin', this.onAnnouncementFocusIn);
    }
    if (this.sliderEl && this.onAnnouncementFocusOut) {
      this.sliderEl.removeEventListener('focusout', this.onAnnouncementFocusOut);
    }
    if (this.swiperSlider && this.onAnnouncementSlideChange) {
      this.swiperSlider.off('slideChange', this.onAnnouncementSlideChange);
      this.swiperSlider.off('activeIndexChange', this.onAnnouncementSlideChange);
      this.swiperSlider.off('slideChangeTransitionEnd', this.onAnnouncementSlideChange);
      this.swiperSlider.off('afterInit', this.onAnnouncementSlideChange);
      this.swiperSlider.off('loopFix', this.onAnnouncementSlideChange);
    }
    if (this.announcementSlideObserver) {
      this.announcementSlideObserver.disconnect();
      this.announcementSlideObserver = null;
    }
    if (this.swiperInstance) this.swiperInstance.slider.destroy();
    if (this.serviceTrigger && this.onServiceTriggerClick) {
      this.serviceTrigger.removeEventListener('click', this.onServiceTriggerClick);
    }
    if (this.serviceMenu && this.onServiceMenuMouseEnter) {
      this.serviceMenu.removeEventListener('mouseenter', this.onServiceMenuMouseEnter);
    }
    if (this.serviceMenu && this.onServiceMenuFocusIn) {
      this.serviceMenu.removeEventListener('focusin', this.onServiceMenuFocusIn);
    }
    if (this.serviceMenu && this.onServiceMenuMouseLeave) {
      this.serviceMenu.removeEventListener('mouseleave', this.onServiceMenuMouseLeave);
    }
    if (this.serviceMenu && this.onServiceMenuFocusOut) {
      this.serviceMenu.removeEventListener('focusout', this.onServiceMenuFocusOut);
    }
    if (this.onDocumentClickForService) {
      document.removeEventListener('click', this.onDocumentClickForService);
    }
    if (this.onDocumentKeyDownForService) {
      document.removeEventListener('keydown', this.onDocumentKeyDownForService);
    }
  }
}
customElements.define('ctm-announcement-container', CtmAnnouncementContainer);
